import React, {useCallback} from 'react';
import {DragDropContext, Droppable, DropResult} from 'react-beautiful-dnd';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import Button from '../../../incase-domain/components/button/Button';
import useCreateNewSlide from '../../../service/hooks/useCreateNewSlide';
import {TEMPLATE} from '../../../service/template/types';
import {swapSelectedSlidesAction} from '../../../store/actions/edit';
import {previewsLoaderSelector, selectedPresentationSlidesSelector} from '../../../store/selectors/presentation.selectors';
import {selectedSlideIdSelector} from '../../../store/selectors/slide.selectors';
import SlidePreviewCard from './SlidePreviewCard';

function NewSlideFooterButton(): JSX.Element {
    const {t} = useTranslation();
    const {createNewSlide} = useCreateNewSlide();

    return (
        <Button
            className="c-slidePreviewFooter__footer_button"
            size="lg"
            variant="ghost"
            leading="ph:plus"
            fullWidth
            color="secondary"
            onClick={() => createNewSlide(TEMPLATE.BLANK, true)}
        >
            {t('presEditPage.newSlide')}
        </Button>
    );
}

const SlidePreviewList = React.memo(function SlidePreviewList(): JSX.Element {
    const dispatch = useDispatch();
    const slides = useSelector(selectedPresentationSlidesSelector);
    const selectedSlideId = useSelector(selectedSlideIdSelector);
    const previewsLoader = useSelector(previewsLoaderSelector);

    const swapSlideOrderHandler = useCallback(
        (newIndex: number, oldIndex: number): void => {
            dispatch(swapSelectedSlidesAction(newIndex, oldIndex));
        },
        [dispatch]
    );

    const onDragEnd = useCallback(
        (e: DropResult): void => {
            if (typeof e.destination?.index !== 'undefined') {
                swapSlideOrderHandler(e.destination.index, e.source.index);
            }
        },
        [swapSlideOrderHandler]
    );

    return (
        <div className="swiper c-slidePreviewFooter__body">
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable" direction="vertical">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="c-slidePreviewFooter__body_wrapper">
                            {slides.map((slide, index) => (
                                <div key={slide._id}>
                                    <SlidePreviewCard
                                        index={index}
                                        displayedSlide={slide}
                                        selectedSlideId={selectedSlideId}
                                        previewsLoader={previewsLoader}
                                    />
                                </div>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
});

function SlidePreviewFooter(): JSX.Element {
    return (
        <div className="c-slidePreviewFooter">
            <SlidePreviewList />
            <div className="c-slidePreviewFooter__footer">
                <NewSlideFooterButton />
            </div>
        </div>
    );
}

export default React.memo(SlidePreviewFooter);
